import "./AssetManagerProjectCardWithTabsTemplate.css";

import { ArrowFullLeftDarkIcon, ArrowheadRightDarkIcon, ClockIcon, EllipseGroupBlueIcon } from "../../assets";
import { InteractionObjectType } from "../../constants";
import { getProjectPhaseLabel } from "../../constants/project-phase";
import { SeedActiveElement, TabsList } from "../../models";
import { RequestProjectAccessModal } from "../../route/asset-manager/projects/components";
import { getFormattedDate } from "../../utils";
import {
  Button,
  Column,
  ExpandableCard,
  InformationBox,
  KanaTooltip,
  LocationFlag,
  PieChart,
  ProfileMenu,
  ProfileMenuItem,
  Row,
  ScrollablePills,
  SeedFooter,
  SeedNavigation,
  Tabs,
} from "../../widget";
import {
  DeleteProjectModal,
  MultiDraftModal,
  PublishProjectModal,
  VersionConflictModal,
} from "../DeveloperProjectCardWithTabsTemplate/components";
import { CancelProjectModal } from "../DeveloperProjectCardWithTabsTemplate/components/CancelProjectModal";
import { UpdateStatusModal } from "../DeveloperProjectCardWithTabsTemplate/components/UpdateStatusModal";
import { NotesPanel } from "../NotesPanel";
import { TagsPanel } from "../TagsPanel";
import { AssetManagerProjectCardWithTabsTemplatePills, InviteDeveloperModal } from "./components";
import { useAssetManagerProjectCardWithTabsTemplate } from "./useAssetManagerProjectCardWithTabsTemplate";

// TODO-JWI: This component is very similar to the DeveloperProjectCardWithTabsTemplate, we should look to abstract out the common components and logic between these two templates into a shared template at some point in the future
interface AssetManagerProjectCardWithTabsTemplateProps {
  activeElement: SeedActiveElement;
  tabsList: TabsList;
  backButtonText?: string;
  handleBackClick?: () => void;
}

export const AssetManagerProjectCardWithTabsTemplate = ({
  activeElement,
  tabsList,
  backButtonText,
  handleBackClick,
}: AssetManagerProjectCardWithTabsTemplateProps): JSX.Element => {
  const {
    setShowQuickActionMenu,
    showQuickActionMenu,
    setShowInviteDeveloperModal,
    showInviteDeveloperModal,
    setShowPublishProjectModal,
    showPublishProjectModal,
    setShowDeleteProjectModal,
    showDeleteProjectModal,
    setShowMultiDraftModal,
    showMultiDraftModal,
    setShowVersionConflictModal,
    showVersionConflictModal,
    setShowNotesPanel,
    showNotesPanel,
    showTagsPanel,
    setShowTagsPanel,
    showUpdateStatusModal,
    setShowUpdateStatusModal,
    showCancelProjectModal,
    setShowCancelProjectModal,
    showRequestProjectAccessModal,
    setShowRequestProjectAccessModal,
    draftActivityHistory,
    newVersionActivityHistory,
    draftActivityHistoryUuid,
    quickActionsMenuOptionsList,
    tags,
    navigateToDraftActivity,
    resumeDraft,
    shouldRefreshProjectDetails,
    shouldRefreshTags,
    currentUserType,
    isGroupProject,
    projectDetails,
    publicProjectDetails,
    nextMilestone,
    isPendingDeveloperInvitationLoading,
    pendingDeveloperInvitation,
    navigateToPendingDeveloperInvitation,
    shouldRefreshPendingDeveloperInvitation,
    pendingProjectAccessRequest,
    isPendingProjectAccessRequestLoading,
    navigateToPendingProjectAccessRequest,
    shouldRefreshPendingProjectAccessRequest,
  } = useAssetManagerProjectCardWithTabsTemplate();

  let developerInvitationContent: JSX.Element;

  if (isPendingDeveloperInvitationLoading) {
    developerInvitationContent = (
      <InformationBox
        variant="loading"
        corners="rounded"
        direction="row"
        hasBorder
        title="Checking invitation status"
      />
    );
  } else if (pendingDeveloperInvitation) {
    developerInvitationContent = (
      <InformationBox
        variant="info"
        corners="rounded"
        direction="row"
        onEdit={navigateToPendingDeveloperInvitation}
        editIcon={<ArrowheadRightDarkIcon />}
        hasBorder
        hasUnderline={false}
        title="Developer invited to project"
      >
        <p className="body1">You&apos;ll be notified when they respond</p>
      </InformationBox>
    );
  } else {
    developerInvitationContent = (
      <InformationBox
        variant="alert"
        corners="rounded"
        direction="row"
        onEdit={() => setShowInviteDeveloperModal(true)}
        editText="Invite developer"
        editIcon={<ArrowheadRightDarkIcon />}
        hasBorder
        hasUnderline={false}
      >
        <p className="body1">
          <b>No developer assigned</b>
        </p>
      </InformationBox>
    );
  }

  const developerInvitationNotice =
    projectDetails && !projectDetails.developer?.uuid ? (
      <Row spacingV="ll">
        <Column span={12}>{developerInvitationContent}</Column>
      </Row>
    ) : null;

  let projectAccessRequestContent: JSX.Element;

  if (isPendingProjectAccessRequestLoading) {
    projectAccessRequestContent = (
      <InformationBox
        variant="loading"
        corners="rounded"
        direction="row"
        hasBorder
        title="Checking access request status"
      />
    );
  } else if (pendingProjectAccessRequest) {
    projectAccessRequestContent = (
      <InformationBox
        variant="info"
        title="Limited view"
        corners="rounded"
        direction="row"
        onEdit={navigateToPendingProjectAccessRequest}
        editText="Access requested"
        editIcon={<ClockIcon />}
        editDirection="reverse"
        hasBorder
        hasUnderline={false}
      >
        <p className="body1">
          Your organisation has requested access on {getFormattedDate(pendingProjectAccessRequest.sentDate)}
        </p>
      </InformationBox>
    );
  } else {
    projectAccessRequestContent = (
      <InformationBox
        variant="info"
        title="Limited view"
        editText="Request access"
        onEdit={() => setShowRequestProjectAccessModal(true)}
        corners="rounded"
        direction="row"
        hasBorder
        hasUnderline={false}
      >
        <p className="body1">You do not have access to view all project data</p>
      </InformationBox>
    );
  }

  return (
    <div className="AssetManagerProjectCardWithTabsTemplate">
      <SeedNavigation activeElement={activeElement} />
      <div className="AssetManagerProjectCardWithTabsTemplateContainer">
        {handleBackClick && (
          <div className="HeaderContainer">
            <Button
              text={backButtonText ?? "Back"}
              variant="secondary"
              icon={<ArrowFullLeftDarkIcon />}
              iconPosition="left"
              onClick={handleBackClick}
              hasNoPadding
              minWidth={false}
            />
          </div>
        )}
        {draftActivityHistory && (
          <Row spacingV="l">
            <Column span={12}>
              <InformationBox variant="info">
                <div className="ProjectActivityNotificationContent">
                  <p className="body2">You have a draft activity that has not been saved. You can resume the draft </p>
                  <button className="BtnLink body2" onClick={navigateToDraftActivity}>
                    here
                  </button>
                </div>
              </InformationBox>
            </Column>
          </Row>
        )}
        <Row spacingV="ml">
          <Column span={12}>
            <div className="HeaderContent">
              <h1>{projectDetails?.displayName ?? publicProjectDetails?.name}</h1>
              {(projectDetails?.addressCountryCode || publicProjectDetails?.addressCountryCode) && (
                <LocationFlag
                  addressCountryCode={
                    (projectDetails?.addressCountryCode ?? publicProjectDetails?.addressCountryCode) as string
                  }
                />
              )}
              {!!projectDetails && quickActionsMenuOptionsList.length > 0 && (
                <div>
                  <Button
                    onClick={() => setShowQuickActionMenu(true)}
                    text="Quick actions"
                    size="smallLarge"
                    variant="tertiary"
                    corners="rounded"
                    icon={<EllipseGroupBlueIcon />}
                  />
                  <ProfileMenu
                    show={showQuickActionMenu}
                    onClose={() => setShowQuickActionMenu(false)}
                    position={{ top: "0" }}
                  >
                    {quickActionsMenuOptionsList.map((el) => (
                      <ProfileMenuItem
                        key={el.id}
                        profileMenuItem={el}
                        onClick={() => {
                          if (el.action) {
                            el.action();
                          }
                          setShowQuickActionMenu(false);
                        }}
                      />
                    ))}
                  </ProfileMenu>
                </div>
              )}
            </div>
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={8}>
            <AssetManagerProjectCardWithTabsTemplatePills />
          </Column>
          {!!projectDetails && (
            <Column span={4} justify="end">
              <ScrollablePills pills={tags} linkText="Tags" linkOnClick={() => setShowTagsPanel(true)} />
            </Column>
          )}
        </Row>
        {!projectDetails && publicProjectDetails ? (
          <Row spacingV="ll">
            <Column span={12}>{projectAccessRequestContent}</Column>
          </Row>
        ) : (
          <>
            {developerInvitationNotice}
            <Row spacingV="ll">
              <Column span={6}>
                <ExpandableCard title="Phase" contentAlignment="end" height="100%" corners="rounded">
                  <div className="PhaseCardContent">
                    <h1>{getProjectPhaseLabel(projectDetails?.cachedPhase)}</h1>
                    {nextMilestone && (
                      <p className="body1">
                        <b>Next milestone: </b>
                        {nextMilestone}
                      </p>
                    )}
                  </div>
                </ExpandableCard>
              </Column>
              <Column span={6}>
                <ExpandableCard
                  title="Units"
                  contentAlignment="center"
                  tooltip={
                    <KanaTooltip
                      tooltipHeader="Units"
                      tooltipText="Please note that only 'issued' units show here, not units listed as 'draft' or 'inactive'"
                    />
                  }
                  corners="rounded"
                  height="100%"
                >
                  <PieChart
                    size={120}
                    data={{
                      Available: projectDetails?.cachedAvailableQuantity || 0,
                      Reserved: projectDetails?.cachedReservedQuantity || 0,
                      Sold: projectDetails?.cachedSoldQuantity || 0,
                    }}
                    noDataMessage="No units issued"
                    isGradient
                    centerTextFont="Fraunces"
                  />
                </ExpandableCard>
              </Column>
            </Row>
          </>
        )}
        <Tabs className="TemplateTabs" tabsList={tabsList} currentUserType={currentUserType} />
        <SeedFooter />
      </div>
      <PublishProjectModal
        showPublishProjectModal={showPublishProjectModal}
        closeModal={() => setShowPublishProjectModal(false)}
      />
      {projectDetails && (
        <UpdateStatusModal
          existingStatus={projectDetails?.status ?? ""}
          isGroupProject={isGroupProject}
          showUpdateStatusModal={showUpdateStatusModal}
          closeModal={() => setShowUpdateStatusModal(false)}
        />
      )}
      <CancelProjectModal
        isGroupProject={isGroupProject}
        showCancelProjectModal={showCancelProjectModal}
        closeModal={() => setShowCancelProjectModal(false)}
        refreshProject={shouldRefreshProjectDetails}
      />
      <DeleteProjectModal
        showDeleteProjectModal={showDeleteProjectModal}
        closeModal={() => setShowDeleteProjectModal(false)}
      />
      <MultiDraftModal
        showMultiDraftModal={showMultiDraftModal}
        resume={resumeDraft}
        closeModal={() => setShowMultiDraftModal(false)}
        draftActivityHistory={draftActivityHistory}
      />
      <VersionConflictModal
        showVersionConflictModal={showVersionConflictModal}
        draftActivityHistoryUuid={draftActivityHistoryUuid}
        closeModal={() => setShowVersionConflictModal(false)}
        newVersionActivityHistory={newVersionActivityHistory}
      />
      <RequestProjectAccessModal
        show={showRequestProjectAccessModal}
        onClose={() => setShowRequestProjectAccessModal(false)}
        onRequestSent={() => shouldRefreshPendingProjectAccessRequest(true)}
      />
      {projectDetails?.uuid && (
        <InviteDeveloperModal
          show={showInviteDeveloperModal}
          onClose={() => setShowInviteDeveloperModal(false)}
          onInvitationSent={shouldRefreshPendingDeveloperInvitation}
          projectUuid={projectDetails.uuid}
        />
      )}
      {projectDetails?.uuid && (
        <NotesPanel
          objectUuid={projectDetails.uuid}
          objectType={InteractionObjectType.PROJECT}
          show={showNotesPanel}
          onClose={() => setShowNotesPanel(false)}
        />
      )}
      {projectDetails?.uuid && (
        <TagsPanel
          objectUuid={projectDetails.uuid}
          objectType={InteractionObjectType.PROJECT}
          show={showTagsPanel}
          onClose={() => setShowTagsPanel(false)}
          shouldRefreshTags={shouldRefreshTags}
        />
      )}
    </div>
  );
};
